import { getTheme, loadTheme, Stack } from '@fluentui/react';
import DirectMemoComposer from 'features/Areas/AreaSwitch/CrossAreaDirectMessaging/DirectMessaging/Actions/DirectMemoComposer/DirectMemoComposer';
import InvitationsManager from 'features/Header/children/InvitationsManager/InvitationsManager';
import Header from 'features/Header/Header';
import { WebBrowserNotification } from 'features/WebBrowserNotification/WebBrowserNotification';
import React, { useEffect } from 'react';
import { ReactElement } from 'react-markdown';
import { getFlag } from 'routes/Flags/Flags';
import {
    useCrossAreaMessageType,
    useShowCrossAreaMessagePanel,
    useShowInvitationsManagerModal,
    useShowSearchBar,
    useWindowWidth
} from 'shared-state/display-options/hooks';
import { DarkTheme } from 'themes/dark';
import { DefaultTheme } from 'themes/default';
import Swal from 'sweetalert2';
import IvicosStrings from 'kits/language/stringKit';
import { isInCypressEnvironment } from 'kits/cypressKit';

interface ICoreViewProps {
    onRenderTopBarDocumentPicker: (textColor?: string, isLowerTopBar?: boolean) => ReactElement;
    children?: React.ReactNode;
}

const CoreView: React.FC<ICoreViewProps> = ({ onRenderTopBarDocumentPicker, children }) => {
    const theme = getTheme();
    //this should belong to a state file...
    const [invitationsManagerModal, setInvitationsManagerModal] = useShowInvitationsManagerModal();
    const [showSearchBar] = useShowSearchBar();
    const [crossAreaMessageType] = useCrossAreaMessageType();
    const [showCrossAreaMessagePanel] = useShowCrossAreaMessagePanel();
    const { smallDevice } = useWindowWidth();

    useEffect(() => {
        getFlag('useDarkMode') == 'YES' ? loadTheme(DarkTheme) : loadTheme(DefaultTheme);

        const handleBeforeUnload = () => {
            sessionStorage.setItem('userInitiated', 'true');
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        if (
            (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming).type === 'reload' &&
            sessionStorage.getItem('userInitiated') === 'true' &&
            // exclude reload due to language change
            !window.location.hash.includes('status') &&
            !isInCypressEnvironment
        ) {
            // created timeout to properly show success icon animation
            setTimeout(() => {
                Swal.fire({
                    icon: 'success',
                    text: `${IvicosStrings.campusRestored}`,
                    confirmButtonText: 'Ok',
                    confirmButtonColor: `${theme.palette.themePrimary}`,
                    customClass: {
                        popup: 'custom-swal-font'
                    }
                });
            }, 1000);
            sessionStorage.removeItem('userInitiated');
        }

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            Swal.close();
        };
    }, []);

    return (
        <Stack style={{ height: '100%', backgroundColor: theme.palette.neutralLight }} tokens={{ childrenGap: 0 }}>
            <Header onRenderDocumentPicker={onRenderTopBarDocumentPicker} />
            {invitationsManagerModal && <InvitationsManager setInvitationsManagerModal={setInvitationsManagerModal} />}
            {(showSearchBar || crossAreaMessageType || showCrossAreaMessagePanel) && <DirectMemoComposer />}
            {!smallDevice && !isInCypressEnvironment && <WebBrowserNotification />}
            {children}
        </Stack>
    );
};

export default CoreView;
